import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, CardContent, FormControlLabel, Stack, Switch, TextField } from '@mui/material';
import PageHeader from '../components/PageHeader.jsx';
import { api } from '../api/client.js';
import { formatApiError, useToast } from '../contexts/ToastContext.jsx';

const ADMIN_DEFAULTS = { company: 'GarmentERP Manufacturing', sessionMinutes: 480, rfidLogin: true };
const USER_DEFAULTS = { dateFormat: 'DD/MM/YYYY', currency: 'PKR', notifications: true };

export default function SettingsPage({ administration = false }) {
  const toast = useToast();
  const defaults = administration
    ? ADMIN_DEFAULTS
    : USER_DEFAULTS;
  const scope = administration ? 'admin' : 'preferences';
  const [values, setValues] = useState(defaults);
  const settings = useQuery({
    queryKey: ['settings', scope],
    queryFn: async () => (await api.get(`/settings/${scope}`)).data
  });
  useEffect(() => {
    if (settings.data) setValues({ ...defaults, ...settings.data });
  }, [settings.data, defaults]);
  const change = (name, value) => setValues((current) => ({ ...current, [name]: value }));

  const save = useMutation({
    mutationFn: async () => {
      if (administration && !values.company?.trim()) throw new Error('Company name is required');
      return api.put(`/settings/${scope}`, values, { skipErrorToast: true });
    },
    onSuccess: () => toast.success('Settings saved successfully'),
    onError: (error) => toast[error.response ? 'error' : 'warning'](
      error.response ? formatApiError(error, 'Unable to save settings') : error.message
    )
  });

  return (
    <>
      <PageHeader
        title={administration ? 'Administration' : 'Settings'}
        subtitle={administration ? 'Company-wide security and operational defaults' : 'Personal display and notification preferences'}
      />
      <Card sx={{ maxWidth: 720 }}><CardContent>
        <Stack spacing={2}>
          {administration ? <>
            <TextField label="Company name" value={values.company} onChange={(e) => change('company', e.target.value)} />
            <TextField label="Session duration (minutes)" type="number" value={values.sessionMinutes} onChange={(e) => change('sessionMinutes', e.target.value)} />
            <FormControlLabel control={<Switch checked={values.rfidLogin} onChange={(e) => change('rfidLogin', e.target.checked)} />} label="Allow authorized RFID login" />
          </> : <>
            <TextField label="Date format" value={values.dateFormat} onChange={(e) => change('dateFormat', e.target.value)} />
            <TextField label="Currency" value={values.currency} onChange={(e) => change('currency', e.target.value)} />
            <FormControlLabel control={<Switch checked={values.notifications} onChange={(e) => change('notifications', e.target.checked)} />} label="Enable operational notifications" />
          </>}
          <Button variant="contained" disabled={save.isPending} onClick={() => save.mutate()}>Save Settings</Button>
        </Stack>
      </CardContent></Card>
    </>
  );
}
